// A labelled, SEARCHABLE multi-select with a Select All row at the top of its
// listing (owner, 2026-09-10).
//
// A `Combobox`, NOT a `Dropdown`. Fluent's Dropdown has no text input, so it
// cannot be searched — fine for three options and useless for 6,000 vendors.
//
// TWO SOURCES, and which one a field uses depends on whether its whole list can
// be held:
//
//   * `options` — the entire list, filtered HERE as the user types. Right for
//     companies (38), accounts (88), departments (42) and codes (269).
//   * `search` — a server query per keystroke, debounced. Required for VENDORS,
//     because there are 6,210 and `MAX_PAGE_LIMIT` is 200: a single request
//     offers 200 of them and silently omits the rest, so a vendor the user knows
//     exists simply is not in the list. That is the same failure
//     `loadAllCodes` exists to avoid, and paging 32 times on mount is the
//     wrong trade for a list this size when the API already searches.
//
// THE SELECT ALL ROW IS AN `<Option>`, NOT A `<Button>`. Fluent renders the
// children inside a Listbox, and a Button in there is skipped by the listbox's
// own arrow-key navigation — it would work with a mouse and not otherwise.
//
// IT APPLIES TO WHAT IS SHOWN, in both modes, and the count in its label is what
// makes that unambiguous: "Select All (12)" after typing `greens` takes the
// twelve matches. On a searched field that is the matches the server returned,
// which is the only set this component can honestly claim to be selecting.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Combobox, Field, Option, OptionGroup, makeStyles, tokens } from '@fluentui/react-components'
import { filterOptions } from '../lib/optionSearch'
import { popupPositionProps, usePopupListboxClass } from './popupPositioning'

/** The sentinel the Select All row carries. Not a legal option value anywhere —
    every real value here is a company id, a vendor id, an account id, a
    department id, a code, or a status. */
const ALL = '__select_all__'

/** How long to wait after a keystroke before querying the server. */
const SEARCH_DEBOUNCE_MS = 250

const useStyles = makeStyles({
  // ONE STYLE, not two merged. `mergeClasses` on two `makeStyles` outputs warns
  // at runtime — "a passed string contains multiple identifiers of atomic
  // classes" — and there is no reason to split these: they describe one control.
  //
  // `minWidth: 0` is what lets the grid actually shrink the cell rather than the
  // Combobox forcing its own intrinsic width and breaking alignment.
  //
  // The `& input` rules keep the field ONE LINE, ALWAYS. A selection of 38
  // companies printed in full grew the cell to twenty lines and pulled every
  // neighbouring control out of alignment — the exact fault this form was
  // rebuilt to fix. `summarize` keeps the text short; this keeps a long single
  // label from doing the same thing.
  //
  // `minmax(0, 1fr)` replaces Fluent's `1fr auto` columns. A bare `1fr` track
  // cannot shrink below the input's natural width (about 171px, from its `size`
  // attribute), so in a narrow cell the chevron was pushed past the border
  // (owner, 2026-10-08, the routing-rule Value field).
  control: {
    minWidth: 0,
    width: '100%',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    '& input': {
      minWidth: 0,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflowX: 'hidden',
    },
  },
  // The option's ID under its name, as `EntityPicker` draws it.
  optionBody: { display: 'flex', flexDirection: 'column' },
  optionSecondary: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
})

export interface MultiSelectOption {
  value: string
  label: string
  /** Optional smaller grey line under the label — the ID, on the Rules →
      Oversight Approval Account, Department and Code pickers. A typed search
      matches it as well as the label (`lib/optionSearch.ts`, D-182). */
  secondary?: string
  /** Alternative wordings a search also matches, beside `secondary`. */
  searchTerms?: string | null
  /** Optional heading to file this option under. Options sharing a group render
      together beneath it, in first-appearance order. */
  group?: string
}

interface Props {
  label: string
  /** The whole list, held and filtered here. Give this OR `search`. */
  options?: readonly MultiSelectOption[]
  /**
   * A server query, for a list too large to hold. Called with the typed term
   * (and once with `''` on mount), debounced.
   *
   * MUST BE A STABLE REFERENCE — wrap it in `useCallback` at the call site, or
   * every render restarts the search.
   */
  search?: (term: string) => Promise<readonly MultiSelectOption[]>
  selected: readonly string[]
  onChange: (values: string[]) => void
  /** Shown when nothing is selected. */
  placeholder?: string
  disabled?: boolean
  /** The name, where the row shows no label of its own (Rules → Oversight
      Approval's condition value, QA 2026-10-04 A-09: otherwise the box was
      named by its placeholder, `e.g. XEROX BUSINESS SOLUTIONS`). */
  ariaLabel?: string
  /** The dialog the field sits in, for its list to mount inside (D-344). A
      list portaled to the page body sits outside the dialog's focus trap and
      snaps shut after a click. Omit it and the field finds its own dialog, as
      `EntityPicker` does (D-351); outside a dialog there is none to find. */
  popupHost?: HTMLElement | null
  /** Names already known for selected ids — a saved rule's `value_labels` —
      so a searched field reads a stored vendor by name before any search has
      listed it (D-350: an Override's vendor rule names any of 6,210 vendors). */
  knownLabels?: Readonly<Record<string, string>>
}

export function MultiSelectField({
  label,
  options,
  search,
  selected,
  onChange,
  placeholder = 'Any',
  disabled,
  ariaLabel,
  popupHost,
  knownLabels,
}: Props) {
  const s = useStyles()
  const popupListbox = usePopupListboxClass()
  const isRemote = search !== undefined

  // THE DIALOG THIS FIELD SITS IN, FOUND BY THE FIELD ITSELF when the caller
  // hands none in (D-351). Rules → Routing's condition rows passed none, so the
  // vendor list portaled to the page body with Fluent's own sizing and opened
  // as a narrow strip pinned to the top of the window, beside the pop-up
  // rather than under the field. Outside a dialog nothing is found and the
  // field keeps Fluent's own placement, as before.
  const controlRef = useRef<HTMLInputElement>(null)
  const [ownHost, setOwnHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setOwnHost((controlRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? null)
  }, [])
  const host = popupHost !== undefined ? popupHost : (ownHost ?? undefined)

  // `null` means "not typing" — the input then shows the summary. An empty
  // STRING is different: the user has cleared the box and is about to type, so
  // the listing must not still be filtered by the old query.
  const [query, setQuery] = useState<string | null>(null)
  // OPEN AND CLOSE ON A CLICK ON THE WORDS (D-344, corrected D-351). Held here
  // so a click can be refused while a search is typed (`onOpenChange` below).
  //
  // Fluent's own text box already opens and closes its list on each click
  // (`useTriggerSlot`'s `onClick`). D-344 added an `onClick` of its own that
  // set the list open after every click, on the reading that a click did
  // nothing; it ran after Fluent's, so it undid every close: a second click on
  // the words left the list open, and it seemed stuck (owner, 2026-10-12:
  // "Clicking in and out of dropdowns is difficult and buggy"). Found by
  // logging `aria-expanded` per click in the Rules → Routing pop-up.
  const [open, setOpen] = useState(false)
  const [remote, setRemote] = useState<MultiSelectOption[]>([])
  const [searching, setSearching] = useState(false)

  // EVERY LABEL EVER SEEN, so a selection made under one search still reads as
  // its name after another search has replaced the listing. Without this the
  // summary would fall back to the raw id the moment the option left the page.
  //
  // STATE, not a ref: `summary` reads it during render, and reading a ref while
  // rendering is exactly what the react-hooks rule forbids — a value the render
  // depends on has to be state or the summary can render stale.
  const [seenLabels, setSeenLabels] = useState<Record<string, string>>({})

  useEffect(() => {
    const run = search
    if (run === undefined) return
    const term = query ?? ''
    let live = true
    setSearching(true)
    // No delay on the initial load; a debounce only after a keystroke.
    const timer = setTimeout(
      () => {
        void run(term)
          .then((list) => {
            if (!live) return
            setSeenLabels((prev) => {
              const next = { ...prev }
              for (const option of list) next[option.value] = option.label
              return next
            })
            setRemote([...list])
          })
          .catch(() => {
            // A failed search leaves the listing empty rather than stale: a
            // stale list would invite picking something the query did not match.
            if (live) setRemote([])
          })
          .finally(() => {
            if (live) setSearching(false)
          })
      },
      query === null ? 0 : SEARCH_DEBOUNCE_MS,
    )
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [search, query])

  const shown = useMemo(() => {
    // A searched field is already filtered by the server; filtering again here
    // would drop rows it matched on a field this component cannot see.
    if (isRemote) return remote
    // Matched on what the user can see: the LABEL, and the SECONDARY line
    // where an option has one (the ID, D-182). `filterOptions` owns the rule.
    return filterOptions(options ?? [], query)
  }, [isRemote, remote, options, query])

  const shownValues = useMemo(() => shown.map((o) => o.value), [shown])
  const allShownSelected = shown.length > 0 && shownValues.every((v) => selected.includes(v))

  /**
   * What the closed control says. NEVER the full selection: see `control` above.
   *
   * One selection reads as itself, because that is the common case and the most
   * useful thing to see; anything more is a count, because a count is the only
   * summary that cannot grow.
   *
   * EMPTY STRING when nothing is selected, NOT the placeholder. The placeholder
   * is the `placeholder` prop's job, and returning it here put the literal text
   * "Any" in the input's VALUE — so the first keystroke produced `Anyappro`.
   */
  const summary = useMemo(() => {
    if (selected.length === 0) return ''
    // "All (N)" only where N is genuinely the whole list. A searched field never
    // knows its own total, so it says a count instead of claiming everything.
    if (!isRemote && options !== undefined && options.length > 0 && selected.length === options.length) {
      return `All (${options.length})`
    }
    if (selected.length === 1) {
      const only = selected[0] as string
      const fromOptions = (options ?? []).find((o) => o.value === only)?.label
      return fromOptions ?? seenLabels[only] ?? knownLabels?.[only] ?? only
    }
    return `${selected.length} selected`
  }, [selected, options, isRemote, seenLabels, knownLabels])

  /** Options in group order, ungrouped ones first. */
  const groups = useMemo(() => {
    const order: string[] = []
    const byGroup = new Map<string, MultiSelectOption[]>()
    for (const option of shown) {
      const key = option.group ?? ''
      if (!byGroup.has(key)) {
        byGroup.set(key, [])
        order.push(key)
      }
      byGroup.get(key)?.push(option)
    }
    return order.map((key) => ({ key, options: byGroup.get(key) ?? [] }))
  }, [shown])

  /** The name first; the ID, where there is one, under it. */
  const renderOption = (option: MultiSelectOption) =>
    option.secondary ? (
      <div className={s.optionBody}>
        <span>{option.label}</span>
        <span className={s.optionSecondary}>{option.secondary}</span>
      </div>
    ) : (
      option.label
    )

  const selectAllLabel = allShownSelected
    ? `Clear All (${shown.length})`
    : `Select All (${shown.length})`

  return (
    <Field label={label}>
      <Combobox
        className={s.control}
        ref={controlRef}
        {...(host !== undefined ? { ...popupPositionProps(host), listbox: { className: popupListbox } } : {})}
        multiselect
        // `freeform` so the typed text is ours to control rather than Fluent's
        // typeahead replacing it with an option label mid-word.
        freeform
        disabled={disabled}
        aria-label={ariaLabel}
        placeholder={placeholder}
        selectedOptions={[...selected]}
        value={query ?? summary}
        onChange={(e) => setQuery(e.target.value)}
        open={open}
        onOpenChange={(e, d) => {
          // NOT WHILE A SEARCH IS TYPED: a click on the words then places the
          // cursor in the search, and closing would throw the search away.
          if (!d.open && query !== null && e.type === 'click' && e.target instanceof HTMLInputElement) return
          setOpen(d.open)
          // Closing drops the search, so the next open starts from the whole
          // list and the input goes back to reading as a summary.
          if (!d.open) setQuery(null)
        }}
        onBlur={() => setQuery(null)}
        onOptionSelect={(_, d) => {
          if (d.optionValue === ALL) {
            const next = allShownSelected
              ? selected.filter((value) => !shownValues.includes(value))
              : [...new Set([...selected, ...shownValues])]
            onChange(next)
            return
          }
          // Fluent hands back the whole new selection; the sentinel can still be
          // in it if a previous click landed on that row, so it is filtered out
          // rather than trusted to be absent.
          onChange(d.selectedOptions.filter((value) => value !== ALL))
        }}
      >
        {shown.length > 0 && (
          <Option value={ALL} text={selectAllLabel}>
            {selectAllLabel}
          </Option>
        )}
        {groups.map(({ key, options: groupOptions }) =>
          key === '' ? (
            groupOptions.map((option) => (
              <Option key={option.value} value={option.value} text={option.label}>
                {renderOption(option)}
              </Option>
            ))
          ) : (
            <OptionGroup key={key} label={key}>
              {groupOptions.map((option) => (
                <Option key={option.value} value={option.value} text={option.label}>
                  {renderOption(option)}
                </Option>
              ))}
            </OptionGroup>
          ),
        )}
        {shown.length === 0 && (
          <Option value="__empty__" disabled text={searching ? 'Searching…' : 'No matches'}>
            {searching ? 'Searching…' : 'No matches'}
          </Option>
        )}
      </Combobox>
    </Field>
  )
}
